"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateHelpers = exports.DateFormatOptions = void 0;
const format_1 = require("date-fns/format");
const isThisWeek_1 = require("date-fns/isThisWeek");
const isThisYear_1 = require("date-fns/isThisYear");
const isToday_1 = require("date-fns/isToday");
const isYesterday_1 = require("date-fns/isYesterday");
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const _1 = require(".");
const Extensions_1 = require("./Extensions");
/**
 * This DateHelper is to be used across all client apps for setting date-related properties (e.g. app-wide setting a timezone) and for formatting dates into human-readable strings
 * The "moment" library is deprecated, so should be replaced by an alternative (suggestion is https://date-fns.org/)
 *
 * There are quite a few methods in here, so see the "SECTION -" parts for navigating around
 */
// SECTION - Formats
exports.DateFormatOptions = {
    /** 5 February 2021 */
    NHS_MANUAL_DATE_FORMAT: "D MMMM YYYY",
    /** 5 Feb 2021 : Used for non DOB related information, such as the date in which a list was created */
    NHS_MANUAL_DATE_ABBREVIATED_FORMAT: "D MMM YYYY",
    /** Friday 5 February 2021 */
    NHS_DATE_LONG_WITH_DAY: "dddd D MMMM YYYY",
    /** 8.22am : Time with dot instead of the usual colon and no spacing before am/pm */
    NHS_TIME_SHORT_12_HR_DOT: "h.mma",
    /** 05 Feb 2021 : Used for basic displaying the date */
    DATE_SHORT_WITH_SPACES: "DD MMM YYYY",
    /** 05 Feb : Used for basic displaying the date without the year */
    DATE_SHORT_WITH_SPACES_NO_YEAR: "DD MMM",
    /** 20210205 : Used for API calls */
    DATE_SHORT_NO_SPACES_REVERSE: "YYYYMMDD",
    /** 05-Feb-2021 : Used for DOBs */
    DATE_SHORT_WITH_HYPHENS: "DD-MMM-YYYY",
    /** 05-02-2021 : Used for DOBs */
    DATE_SHORT_NUMERIC_WITH_HYPHENS: "DD-MM-YYYY",
    /** 2021-2-5 : Used for DOBs */
    DATE_SHORT_NUMERIC_WITH_HYPHENS_REVERSE: "YYYY-MM-DD",
    /** 05/02/2021 : Used small display */
    DATE_SHORT_WITH_SLASH: "DD/MM/YYYY",
    /** Friday, 05 February 2021 */
    DATE_LONG: "dddd, DD MMMM YYYY",
    /** Friday, 5 February */
    DATE_LONG_SHORT_DAY: "dddd, D MMMM",
    /** Friday 5th Feb, 2021 */
    DATE_LONG_ORDINAL: "dddd Do MMM, YYYY",
    /** Friday 5th February, 2021 */
    DATE_LONG_ORDINAL_WITH_MONTH: "dddd Do MMMM, YYYY",
    /** Fri 05 Feb, 2021 : Used for date dropdown options e.g. in PatientLists */
    DATE_DROPDOWN_OPTION_FORMAT: "ddd DD MMM, YYYY",
    /** 05 */
    DATE_SHORT_DAY: "DD",
    /** 02 */
    DATE_SHORT_MONTH: "MM",
    /** 2021 */
    DATE_SHORT_YEAR: "YYYY",
    /** 05 Feb 2021 - 08:22:55 : Used to display date time with precision */
    DATE_TIME_WITH_SECONDS: "DD MMM YYYY - HH:mm:ss",
    /** 8:22am - 5 February 2021 : Very precise but human readable format, used for presenting audit trails for example */
    HUMAN_READABLE_TIME_DATE: "h:mma - D MMMM YYYY",
    /** 5 Feb 2021 - 08:22 : Very precise but human readable format, used for presenting message date & time for example */
    HUMAN_READABLE_DATE_TIME: "D MMM YYYY - HH:mm",
    /** 08:22, 05 Feb 2021 : A 24 hour + human readable date */
    TIME_DATE: "HH:mm, DD MMM YYYY",
    /** Friday 5th February, 2021 - 08:22 */
    TIME_DATE_LONG: "dddd Do MMMM, YYYY - HH:mm",
    /** 08:22 Fri 5th Feb 2021 : A 24 hour + date with day of the week */
    TIME_DATE_LONG_WITH_DAY: "HH:mm ddd Do MMM YYYY",
    /** Friday 5th February, 2021 at 08:22 */
    TIME_DATE_LONG_WITH_AT_DAY: "dddd Do MMMM, YYYY [at] HH:mm",
    /** Friday, 5 February at 8:22 am */
    TIME_DATE_LONG_WITH_AT_SHORT_DAY: "dddd, D MMMM [at] H:mm a",
    /** 08:22:55 - 05/02/2021 : Time and Date - used in messages */
    TIME_DATE_SHORT: "HH:mm:ss - DD/MM/YYYY",
    /** 05/02/2021, 08:22 : Date and Time - used in appointment reminders */
    DATE_TIME_SHORT_WITH_SLASH: "DD/MM/YYYY, HH:mm",
    /** 08:22 : Time only */
    TIME: "HH:mm",
    /** 08:22:55 : Time only with seconds */
    TIME_WITH_SECONDS: "HH:mm:ss",
    /** 08:22 AM : Time only with AM/PM */
    TIME_12_HR: "hh:mm A",
    /** 8:22 am */
    TIME_SHORT_12_HR: "h:mm a",
    /** 2021-02-05T08:22:55.5555+00:00 : ISO string date format - used to validate dates */
    ISO_DATE_STRING: "YYYY-MM-DDTHH:mm:ss.sssZ",
    /** 2021-02-05T08:22 : ISO string date format - used to validate dates */
    ISO_DATE_STRING_WITHOUT_SECONDS: "YYYY-MM-DDTHH:mm",
};
const UnrecognisedDateStringFormat = "Unspecified";
class DateHelpers {
    // SECTION - Getting some info
    /**
     * @param ISODate date string in ISO format
     * @return whether date is a ISO compatible format
     * */
    static isISODate(ISODate) {
        return (0, moment_timezone_1.default)(ISODate, moment_timezone_1.default.ISO_8601).isValid();
    }
    /**
     * Get how many days ago a date/time was.
     * Only accepts ISO compatible date
     *
     * @param date date string in ISO format
     * @example today with any time will return 0
     * @example yesterday with any time will return 1
     * @example any time on the date N days ago will return N
     */
    static getDaysFromEndOfToday(date) {
        const midnightTonight = _a.getEndOfToday();
        const endOfDayOfInputDate = (0, moment_timezone_1.default)(date).utc().endOf("day");
        return midnightTonight.diff(endOfDayOfInputDate, "days");
    }
    /**
     * @param dateA date string in ISO format - start time
     * @param dateB date string in ISO format - end time
     * @return number of minutes between dateA and dateB (positive if start is before end)
     * */
    static getTimeDiffInMinutes(dateA, dateB) {
        return (0, moment_timezone_1.default)(dateB).diff(dateA, "minutes");
    }
    /**
     * @param dateA date string in ISO format - start time
     * @param dateB date string in ISO format - end time
     * @return number of days between dateA and dateB (positive if start is before end)
     * */
    static getNumberOfDaysBetweenTwoDates(dateA, dateB) {
        return (0, moment_timezone_1.default)(dateB).diff(dateA, "days");
    }
    static getDaysFromToday(date) {
        return (0, moment_timezone_1.default)().diff(date, "days");
    }
    /**
     * @param date date string in ISO format
     * @param hours number of hours to check if date is in eg 12
     * @return true if date is within last eg 12 hours
     * */
    static isInLastXHours(date, hours) {
        return (0, moment_timezone_1.default)(date).isAfter((0, moment_timezone_1.default)().subtract(hours, "hours"));
    }
    /**
     * @param date date string in ISO format
     * @return true if date minutes is a multiple of 10
     * */
    static timeDivisibleBy10(date) {
        return (0, moment_timezone_1.default)(date).minutes() % 10 === 0;
    }
}
exports.DateHelpers = DateHelpers;
_a = DateHelpers;
// SECTION - Setting app-wide properties
/**
 * Sets a default timezone for all moment usages across the app. To check all
 * available timezone names you can do console.log(moment.tz.names());
 */
DateHelpers.setDefaultMomentTimezone = (timeZone = "Europe/London") => {
    moment_timezone_1.default.tz.setDefault(timeZone);
};
/**
 * Function to define the moment locale for en-gb
 * This should be run once at the application level and the settings will be
 * available for all usage of moment()
 *
 * Calendar definition ensures all `moment(date).calendar()` usage
 * will follow the following format
 * Today at 10:01 am
 * Yesterday at 10:01 am
 * Tomorrow at 10:01 am
 * All other days: 1 Jan 2021 - 10:01 am
 */
DateHelpers.setMomentLocale = () => {
    moment_timezone_1.default.locale("en-gb", {
        calendar: {
            lastDay: "[Yesterday at] hh:mm a",
            sameDay: "[Today at] hh:mm a",
            nextDay: "[Tomorrow at] hh:mm a",
            lastWeek: "D MMM YYYY - hh:mm a",
            nextWeek: "D MMM YYYY - hh:mm a",
            sameElse: "D MMM YYYY - hh:mm a",
        },
    });
};
DateHelpers.getTimeZone = () => {
    return moment_timezone_1.default.tz.guess() || "Europe/London";
};
// SECTION - Creating a date/time
/**
 * Given an input datestring in one of the supported formats, will construct
 * a date instance (currently moment). Support input date formats are
 *
 * API DOB format -> i.e. "DD-MMM-YYYY"
 * ISO8601 UTC -> i.e. "2020-09-16T13:31:18.6900000Z"
 */
DateHelpers.createDateFromDateString = (date) => {
    const acceptedIncomingFormats = [
        moment_timezone_1.default.ISO_8601,
        exports.DateFormatOptions.DATE_SHORT_WITH_HYPHENS,
        exports.DateFormatOptions.DATE_SHORT_NUMERIC_WITH_HYPHENS,
        exports.DateFormatOptions.DATE_SHORT_WITH_SLASH,
    ];
    return (0, moment_timezone_1.default)(date, acceptedIncomingFormats, true);
};
/**
 *
 * @param date a date in some string format
 * @param formats an array of formats to check i.e. ["DD/MM/YYYY","DD/MM/YY"]
 * @returns true if the date is in one of the provided formats
 */
DateHelpers.checkDateFormats = (date, formats) => {
    return (0, moment_timezone_1.default)(date, formats, true).isValid();
};
/**
 *
 * @param day the day of the date (i.e. 24)
 * @param month the month of the date (i.e. 1)
 * @param year the year of the date (i.e. 1999)
 * @returns a date in ISO string format
 */
DateHelpers.getDateISOStringFromDayMonthYear = (day, month, year) => {
    // Before trying to convert the date into ISO string
    // we need to ensure the date parts are valid integers greater than 0
    const isDateValid = [day, month, year].every((datePart) => typeof datePart === "number" &&
        datePart !== 0 &&
        !isNaN(datePart));
    if (isDateValid) {
        // Month is a 0 index value, so we need to subtract 1 from the month parameter
        return new Date(year, month - 1, day).toISOString();
    }
    return "";
};
/**
 *
 * @param dateTimeString date and time in some string format
 * @param formats possible formats to check the dateTimeString for
 * @param doNotConvertUtc is whether to convert to utc or not
 * @returns a date in ISO string format
 */
DateHelpers.getDateISOStringFromAnotherStringFormat = (dateTimeString, formats, doNotConvertUtc = false) => {
    const dateTime = (0, moment_timezone_1.default)(`${dateTimeString}`, formats, true);
    if (dateTime.isValid()) {
        return dateTime.toISOString(doNotConvertUtc);
    }
    return "";
};
// SECTION - Date of birth
/**
 * Validing if a date of birth is valid (aka a valid date, not in the future)
 * @param d day string
 * @param m month string
 * @param y year string
 * @returns If the date is a valid DOB
 */
DateHelpers.validateDob = (d, m, y) => {
    const dob = `${d}/${m}/${y}`;
    const momentToday = (0, moment_timezone_1.default)(new Date(), "DD/MM/YYYY");
    const momentDob = (0, moment_timezone_1.default)(dob, "DD/MM/YYYY");
    if (momentDob.isAfter(momentToday, "day")) {
        return Extensions_1.WrappedResult.Fail("A date of birth cannot be in the future");
    }
    const numericalYear = parseInt(y, 10);
    const thisYearPlusOne = new Date().getUTCFullYear();
    if (numericalYear < 1900 ||
        numericalYear > thisYearPlusOne ||
        momentDob.isValid() === false) {
        return Extensions_1.WrappedResult.Fail("Please use a valid date, month, and year combination");
    }
    return Extensions_1.WrappedResult.Success({});
};
/**
 * Get the age from the date of birth.
 *
 * @param birthDate date of birth in ISO string format
 * @returns the age worked out from the birth date day
 */
DateHelpers.getAgeFromDate = (birthDate) => {
    if (!(0, moment_timezone_1.default)(birthDate, exports.DateFormatOptions.ISO_DATE_STRING).isValid()) {
        return NaN;
    }
    return (0, moment_timezone_1.default)().diff(birthDate, "years");
};
/**
 * Returns text which will describe the day of an event in the past.
 * @param numberOfDaysAgo The number of days ago, where 0 would be an event that happened today
 * @returns "today", "yesterday" or "within the last N days"
 * @example getDaysAgoText(0) returns "today"
 * @example getDaysAgoText(1) returns "yesterday"
 * @example getDaysAgoText(2) returns "within the last 3 days"
 * @example getDaysAgoText(3) returns "within the last 4 days"
 */
DateHelpers.getDaysAgoText = (numberOfDaysAgo) => {
    if (numberOfDaysAgo === 0) {
        return "today";
    }
    else if (numberOfDaysAgo === 1) {
        return "yesterday";
    }
    else {
        return `within the last ${numberOfDaysAgo + 1} days`;
    }
};
/**
 *
 * @param date date string in ISO format
 * @returns An object with the date separated into it's 3 parts (day, month, year)
 */
DateHelpers.getDateParts = (date) => {
    if (date) {
        const momentDate = _a.createDateFromDateString(date);
        if (momentDate && momentDate.isValid()) {
            const d = momentDate.date();
            const m = momentDate.month() + 1;
            const y = momentDate.year();
            return { d, m, y };
        }
    }
    return false;
};
DateHelpers.getEndOfToday = () => (0, moment_timezone_1.default)().utc().endOf("day");
// SECTION - Comparisons
/**
 * Only accepts ISO compatible dates.
 * @param date date string in ISO format
 * @return whether the date is before today. If non-ISO date string is passed, it will fallback to `false`.
 */
DateHelpers.isBeforeToday = (date) => {
    if ((0, moment_timezone_1.default)(date).isValid()) {
        return (0, moment_timezone_1.default)(date).isBefore((0, moment_timezone_1.default)(), "day");
    }
    return false; // assume false if date is not valid
};
/**
 * Only accepts ISO compatible dates.
 * @param date date string in ISO format
 * @return whether the date is after today. If non-ISO date string is passed, it will fallback to `false`.
 */
DateHelpers.isAfterToday = (date) => {
    if ((0, moment_timezone_1.default)(date).isValid()) {
        return (0, moment_timezone_1.default)(date).isAfter((0, moment_timezone_1.default)(), "day");
    }
    return false; // assume false if date is not valid
};
/**
 * Only accepts ISO compatible dates.
 * @param dateA date string in ISO format
 * @param dateB date string in ISO format
 * @return whether dateA is after dateB. If non-ISO date string is passed, it will fallback to `false`.
 */
DateHelpers.isAfter = (dateA, dateB) => {
    if ((0, moment_timezone_1.default)(dateA).isValid() && (0, moment_timezone_1.default)(dateB).isValid()) {
        return (0, moment_timezone_1.default)(dateA).isAfter(dateB);
    }
    return false; // false if either date is invalid
};
/**
 * Only accepts ISO compatible dates.
 * @param dateA date string in ISO format
 * @param dateB date string in ISO format
 * @return whether dateA is before dateB. If non-ISO date string is passed, it will fallback to `false`.
 */
DateHelpers.isBefore = (dateA, dateB) => {
    if ((0, moment_timezone_1.default)(dateA).isValid() && (0, moment_timezone_1.default)(dateB).isValid()) {
        return (0, moment_timezone_1.default)(dateA).isBefore(dateB);
    }
    return false; // false if either date is invalid
};
/**
 * Only accepts ISO compatible dates.
 * @param dateA date string in ISO format
 * @param dateB date string in ISO format
 * @return whether dateA and dateB share the same date. If non-ISO date string is passed, it will fallback to `false`
 */
DateHelpers.isSameDate = (dateA, dateB) => {
    if ((0, moment_timezone_1.default)(dateA).isValid() && (0, moment_timezone_1.default)(dateB).isValid()) {
        return (0, moment_timezone_1.default)(dateA).isSame(dateB, "day");
    }
    return false; // false if either date is invalid
};
/**
 * Only accepts ISO compatible dates
 * @param dateA date string in ISO format
 * @param dateB date string in ISO format
 * @return sorter function for sorting most recent first
 **/
DateHelpers.mostRecentSorter = (dateA, dateB) => {
    return (0, moment_timezone_1.default)(dateA) <= (0, moment_timezone_1.default)(dateB) ? -1 : 1;
};
// SECTION - Formatting - Date + Time
/**
 *
 * @returns Current dateTime in ISO string format
 */
DateHelpers.getCurrentTimeStamp = () => {
    return (0, moment_timezone_1.default)().format();
};
/**
 *
 * @param date date string in ISO format
 * @return the time in format according to the Calendar definition (see calendar in setMomentLocale) "Invalid date" if param is not a valid ISO date
 */
DateHelpers.getLocalisedDateTime = (date) => (0, moment_timezone_1.default)(date).calendar();
/**
 * Function to return the date time in format to show times only if today, otherwise to not
 * show the time, use the word "Yesterday" and only show years if not the current year
 * E.g.
 * Today: 10:01 am
 * Yesterday: Yesterday
 * Within current year: 1 Jan
 * Previous year: 31 Dec 2022
 *
 * @param date date string in ISO format
 */
DateHelpers.getLocalisedDateTimeShort = (date) => (0, moment_timezone_1.default)(date).calendar({
    lastDay: "[Yesterday]",
    sameDay: "h:mma",
    lastWeek: function (now) {
        return _a.getDateFormatShort(this, now);
    },
    sameElse: function (now) {
        return _a.getDateFormatShort(this, now);
    },
});
/**
 * Returns the current date in short format, removing the year if it is the current year
 * i.e. 4 Jan or 31 Dec 2022
 * @param date - the date to format
 * @param now - the current date time
 */
DateHelpers.getDateFormatShort = (date, now) => {
    if ((date === null || date === void 0 ? void 0 : date.year()) === (now === null || now === void 0 ? void 0 : now.year())) {
        return "D MMM";
    }
    else {
        return "D MMM YYYY";
    }
};
/**
 * Date with recency bias, aka shows the time if it's recent and date otherwise. This is useful for inbox layouts
 * @param date date string in ISO format
 * @returns a time in format 03:10 PM if it's the same day as today, and a date in format 13 Jul 2021 if it's before today
 */
DateHelpers.getRecentTimestamp = (timestamp) => {
    // If not today then return the date
    if (!timestamp) {
        return null;
    }
    if (_a.isBeforeToday(timestamp)) {
        return _a.formatDate(timestamp, exports.DateFormatOptions.DATE_SHORT_WITH_SPACES);
    }
    // If today then return the timestamp
    return _a.formatTime(timestamp, exports.DateFormatOptions.TIME_12_HR);
};
// SECTION - Formatting - Date only
/**
 *
 * @param date date string in ISO format
 * @param format required date format, default is 28 June 2022
 * @returns
 */
DateHelpers.formatDate = (date, format, timezone = "Europe/London") => {
    const dateObject = _a.createDateFromDateString(date.trim());
    if (dateObject.isValid()) {
        return dateObject.tz(timezone).format(format).toString();
    }
    // temporary - we want to build a picture of the types of date formats
    // being supplied to this function, as it doesn't impose any restrictions
    // on the argument, but does internally restrict for certain formats.
    // as we discover new incoming formats, we will widen what is accepted
    // or fix the callsite. eventually this warning should be removed and
    // the restrictions enforced by the type system.
    _1.Log.warn(new Error("Unrecognised date format: Date string does not match accepted formats"), {
        tags: {
            logger: "DateHelpers.formatDate",
            argument: date,
            requestedFormat: format,
        },
    });
    return UnrecognisedDateStringFormat;
};
DateHelpers.formatDateConditionally = (date, { formatToday, formatThisYear, formatFallback, }, timezone = "Europe/London") => {
    const getFormat = () => {
        if ((0, moment_timezone_1.default)().isSame(date, "day") && formatToday) {
            return formatToday;
        }
        if ((0, moment_timezone_1.default)().isSame(date, "year") && formatThisYear) {
            return formatThisYear;
        }
        return formatFallback;
    };
    return _a.formatDate(date, getFormat(), timezone);
};
/**
 * Using parseZone to use server not user device timezone
 * @param date
 * @return a date string formatted as "Friday, 12 February 2021" or "Invalid date" if wrong date format is passed
 **/
DateHelpers.formatLongDateWithServerTimezone = (date) => moment_timezone_1.default.parseZone(date).format("dddd, DD MMMM YYYY");
/**
 *
 * @param date date string in ISO format
 * @returns year as number, e.g. 2022
 */
DateHelpers.getDateYear = (date) => {
    const dateMoment = (0, moment_timezone_1.default)(date);
    if (date && dateMoment.isValid()) {
        return (0, moment_timezone_1.default)(date).year();
    }
    return NaN;
};
/**
 *
 * @param date date string in ISO format
 * @returns month as number, e.g. 5, notes that this is zero-indexes, so 5 = June
 */
DateHelpers.getDateMonth = (date) => {
    const dateMoment = (0, moment_timezone_1.default)(date);
    if (date && dateMoment.isValid()) {
        return (0, moment_timezone_1.default)(date).month() + 1;
    }
    return NaN;
};
/**
 *
 * @param date date string in ISO format
 * @returns day as number, e.g. 28
 */
DateHelpers.getDateDay = (date) => {
    const dateMoment = (0, moment_timezone_1.default)(date);
    if (date && dateMoment.isValid()) {
        return (0, moment_timezone_1.default)(date).date();
    }
    return NaN;
};
DateHelpers.formatDateRelativeToToday = (s) => {
    const d = new Date(s);
    if ((0, isToday_1.isToday)(d))
        return "Today";
    if ((0, isYesterday_1.isYesterday)(d))
        return "Yesterday";
    // If it's earlier this week: Tuesday
    // Start the week on monday (the default is sunday)
    if ((0, isThisWeek_1.isThisWeek)(d, { weekStartsOn: 1 }))
        return (0, format_1.format)(d, "EEEE");
    // If it's earlier this year: Tue 21 Mar
    if ((0, isThisYear_1.isThisYear)(d))
        return (0, format_1.format)(d, "E d MMM");
    // If it's before this year: 3 Mar 1989
    return (0, format_1.format)(d, "d MMM y");
};
// SECTION - Formatting - Time only
/**
 *
 * @param date date string in ISO format
 * @param format required time format, default is 24h, aka 16:31
 * @returns
 */
DateHelpers.formatTime = (date, format, timezone = "Europe/London") => {
    const dateObject = _a.createDateFromDateString(date);
    if (dateObject.isValid()) {
        return dateObject.tz(timezone).format(format);
    }
    return UnrecognisedDateStringFormat;
};
/**
 * Format a date to return only the time. Using parseZone to use server not user device timezone
 * @param date ie: "2021-06-20T14:10:00+01:00"
 * @return ie: "14:10"
 **/
DateHelpers.formatTimeWithServerTimezone = (date) => moment_timezone_1.default.parseZone(date).format("HH:mm");
/**
 * Pass in any date format. Using parseZone to use server not user device timezone
 * @param date ie: "2021-06-20T14:10:00+01:00"
 * @param format required date format - DateFormatOptions
 * @return
 * TODO: Replace formatTimeWithServerTimezone and formatLongDateWithServerTimezone with the below
 * function
 **/
DateHelpers.formatWithServerTimezone = (date, format) => moment_timezone_1.default.parseZone(date).format(format);
/**
 * Pass in any date format. Using parseZone to use server not user device timezone
 * @param date ie: "2021-06-20T14:10:00+01:00"
 * @param format required date format - DateFormatOptions
 * @return
 **/
DateHelpers.getAndFormatDateForDaysBeforeToday = (days, format) => (0, moment_timezone_1.default)().subtract(days, "days").format(format);
/**
 * Pass in any date format and add minutes to return a time
 * @param date ie: "2021-06-20T14:10:00+01:00"
 * @param minutes number of minutes to add
 * @returns ie: "11:00"
 **/
DateHelpers.addMinsToDateAndFormatTime = (date, minutes, timezone = "Europe/London") => (0, moment_timezone_1.default)(date)
    .add(minutes, "minute")
    .tz(timezone)
    .format(exports.DateFormatOptions.TIME);
//# sourceMappingURL=DateHelper.js.map